<script setup>
/**
 * 上映スケジュールAI ステップ2：本館上映作品・バージョン登録（DEV-14982）
 *
 * - 作品マスタからタイトル検索 → 本館上映作品として登録
 * - 本館登録作品ごとに上映期間・上映時間・上映バージョン（複数）を編集
 * - 登録解除は本館の登録のみ削除（作品マスタは削除されない）
 * - 「詳細」で作品の実績（観客属性・地域充填率・本館実績）を行の下に表示（検索結果・登録済み一覧とも）
 * 対象劇場はバックエンドがログインアカウントから決める。
 */
import { ref, computed, onMounted, nextTick } from "vue";
import { ElMessage, ElMessageBox } from "element-plus";
import {
  getScheduleAiMovieOptionsService,
  searchScheduleAiMovieService,
  getScheduleAiMovieListService,
  addScheduleAiMovieService,
  updateScheduleAiMovieService,
  removeScheduleAiMovieService,
  getScheduleAiMovieVersionSuggestionsService,
} from "@/api/dp/index.js";
import MovieInsight from "./components/movie-insight.vue";

// 上映時間（分）の上下限
const DURATION_MIN = 1;
const DURATION_MAX = 600;
// 検索結果の 1 ページ件数
const SEARCH_PAGE_SIZE = 20;

const loading = ref(false);
// 取得失敗時のメッセージ（劇場未紐付けアカウントなど）。値があれば本文を出さない
const loadErrorMsg = ref("");
const movies = ref([]);
const options = ref({
  dimensions: [],
  screenFormats: [],
  languages: [],
  specials: [],
});

/**
 * バージョン組み合わせの同一判定キー（special 未選択は空文字に正規化）
 */
const versionKey = (v) => `${v.dimension}|${v.screenFormat}|${v.language}|${v.special || ""}`;

/**
 * バージョン組み合わせの表示名。例：「2D・IMAX・字幕」「3D・通常・吹替・爆音」
 */
const versionLabel = (v) => [v.dimension, v.screenFormat, v.language, v.special].filter(Boolean).join("・");

/**
 * 本館上映期間の表示。未入力の開始は全国公開日から
 */
const periodLabel = (row) => {
  if (!row.localStartDate && !row.localEndDate) return "全国公開日から";
  return `${row.localStartDate || "全国公開日"} 〜 ${row.localEndDate || ""}`;
};

const fetchList = async () => {
  const res = await getScheduleAiMovieListService();
  movies.value = Array.isArray(res?.data) ? res.data : [];
};

const fetchAll = async () => {
  loading.value = true;
  loadErrorMsg.value = "";
  try {
    const [optionRes] = await Promise.all([getScheduleAiMovieOptionsService(), fetchList()]);
    const opt = optionRes?.data || {};
    options.value = {
      dimensions: opt.dimensions || [],
      screenFormats: opt.screenFormats || [],
      languages: opt.languages || [],
      specials: opt.specials || [],
    };
  } catch (e) {
    // code=500 のメッセージは request.js 側でトースト済み。画面にも残しておく
    loadErrorMsg.value = e?.message || "データの取得に失敗しました";
  } finally {
    loading.value = false;
  }
};

// ==================== 作品検索・登録 ====================

const keyword = ref("");
// 実際に検索したキーワード（ページ切り替え時はこちらを使う）
const searchedKeyword = ref("");
const searchLoading = ref(false);
const searchRows = ref([]);
const searchTotal = ref(0);
const searchQuery = ref({ pageNum: 1, pageSize: SEARCH_PAGE_SIZE });
// 登録処理中の movieId（ボタンのローディング用）
const addingMovieId = ref(null);
// 詳細（作品の実績）を展開中の検索結果の movieId（1 件ずつ表示）
const detailMovieId = ref(null);

/**
 * 検索結果の詳細を開閉する（未登録の作品でも実績 API は DP 作品 ID で取得できる）
 */
const toggleDetail = (row) => {
  detailMovieId.value = detailMovieId.value === row.movieId ? null : row.movieId;
};

const runSearch = async () => {
  searchLoading.value = true;
  // ページ・キーワードが変わると対象の行が消えるため詳細は閉じる
  detailMovieId.value = null;
  try {
    const res = await searchScheduleAiMovieService({
      keyword: searchedKeyword.value,
      ...searchQuery.value,
    });
    searchRows.value = res?.rows || [];
    searchTotal.value = res?.total || 0;
  } catch {
    // エラーメッセージは request.js でトースト済み
  } finally {
    searchLoading.value = false;
  }
};

const handleSearch = () => {
  const word = keyword.value.trim();
  if (!word) {
    ElMessage.warning("作品タイトルを入力してください");
    return;
  }
  searchedKeyword.value = word;
  searchQuery.value.pageNum = 1;
  runSearch();
};

const clearSearch = () => {
  keyword.value = "";
  searchedKeyword.value = "";
  searchRows.value = [];
  searchTotal.value = 0;
  detailMovieId.value = null;
};

/**
 * 検索結果の登録済みフラグを更新（登録・解除後に一覧と表示を揃える）
 */
const markRegistered = (movieId, registered) => {
  const row = searchRows.value.find((r) => r.movieId === movieId);
  if (row) row.registered = registered;
};

const handleAdd = async (row) => {
  addingMovieId.value = row.movieId;
  try {
    const res = await addScheduleAiMovieService(row.movieId);
    markRegistered(row.movieId, true);
    await fetchList();
    ElMessage.success(`「${row.title}」を登録しました。上映バージョンを設定してください`);
    // 登録直後はバージョンが空のため、そのまま編集ダイアログを開く
    const created = movies.value.find((m) => m.id === res?.data?.id) || res?.data;
    if (created) openEdit(created);
  } catch {
    // 登録済みなどのエラーメッセージは request.js でトースト済み
  } finally {
    addingMovieId.value = null;
  }
};

// ==================== 登録済み一覧の詳細 ====================

const movieTableRef = ref(null);
// 詳細（展開行）を開いている登録行の id。表の展開状態と合わせてボタンの文言を切り替える
const expandedIds = ref(new Set());

/**
 * 「興行分析データ」ボタンで展開行を開閉する（展開アイコンは出さず、ボタンだけで操作する）
 */
const toggleRowDetail = (row) => {
  movieTableRef.value?.toggleRowExpansion(row);
};

/**
 * 表の展開状態が変わったら開いている行の id を取り直す（再取得でデータが入れ替わっても row-key で維持される）
 */
const onExpandChange = (_row, expandedRows) => {
  expandedIds.value = new Set((expandedRows || []).map((r) => r.id));
};

// ==================== 登録解除 ====================

const handleRemove = async (row) => {
  try {
    await ElMessageBox.confirm(
      `「${row.title}」を本館の上映作品から外しますか？登録したバージョンも削除されます。（全体マスタは削除されません）`,
      "確認",
      { confirmButtonText: "登録解除", cancelButtonText: "キャンセル", type: "warning" },
    );
  } catch {
    return;
  }
  try {
    await removeScheduleAiMovieService(row.id);
    ElMessage.success("登録を解除しました");
    markRegistered(row.movieId, false);
    await fetchList();
  } catch {
    // エラーメッセージは request.js でトースト済み
  }
};

// ==================== 編集ダイアログ ====================

const editVisible = ref(false);
const editSaving = ref(false);
const suggestLoading = ref(false);
const editFormRef = ref(null);
// 編集対象の登録行（一覧の要素）
const editTarget = ref(null);
const editForm = ref({
  durationMinute: undefined,
  localStartDate: null,
  localEndDate: null,
  versions: [],
});
// 未保存変更の検知用スナップショット
const editSnapshot = ref("");

// v-for のキー用ローカル連番（versionId は新規行で null のため使えない）
let localKeySeq = 0;
const nextKey = () => {
  localKeySeq += 1;
  return `ver-${localKeySeq}`;
};

/**
 * API のバージョンを編集行に変換（既存は versionId を保持）
 */
const toEditVersion = (v = {}) => ({
  _key: nextKey(),
  versionId: v.versionId ?? null,
  dimension: v.dimension || "",
  screenFormat: v.screenFormat || "",
  language: v.language || "",
  special: v.special || null,
});

// 作品マスタに上映時間がない場合は必須
const durationRequired = computed(() => {
  const master = editTarget.value?.masterDurationMinute;
  return master === null || master === undefined;
});

/**
 * PUT 用のリクエストボディ。新規行は versionId を付けない
 */
const buildEditPayload = () => ({
  durationMinute: Number.isInteger(editForm.value.durationMinute) ? editForm.value.durationMinute : null,
  localStartDate: editForm.value.localStartDate || null,
  localEndDate: editForm.value.localEndDate || null,
  versions: editForm.value.versions.map((v) => {
    const item = {
      dimension: v.dimension,
      screenFormat: v.screenFormat,
      language: v.language,
      special: v.special || null,
    };
    if (v.versionId !== null && v.versionId !== undefined) item.versionId = v.versionId;
    return item;
  }),
});

const isEditDirty = computed(() => !!editSnapshot.value && JSON.stringify(buildEditPayload()) !== editSnapshot.value);

const openEdit = (row) => {
  editTarget.value = row;
  editForm.value = {
    // 本館の上映時間 → 作品マスタの上映時間の順で初期値にする
    durationMinute: row.durationMinute ?? row.masterDurationMinute ?? undefined,
    localStartDate: row.localStartDate || null,
    localEndDate: row.localEndDate || null,
    versions: (row.versions || []).map(toEditVersion),
  };
  // バージョンがなければ入力用に 1 行用意する
  if (!editForm.value.versions.length) editForm.value.versions.push(toEditVersion());
  editSnapshot.value = JSON.stringify(buildEditPayload());
  editVisible.value = true;
  nextTick(() => editFormRef.value?.clearValidate());
};

const closeEdit = () => {
  editVisible.value = false;
  editTarget.value = null;
  editSnapshot.value = "";
};

// 未保存の変更があれば閉じる前に確認する
const beforeEditClose = async (done) => {
  if (isEditDirty.value) {
    try {
      await ElMessageBox.confirm("保存されていない変更があります。破棄して閉じますか？", "確認", {
        confirmButtonText: "破棄して閉じる",
        cancelButtonText: "キャンセル",
        type: "warning",
      });
    } catch {
      return;
    }
  }
  done();
  closeEdit();
};

const addVersionRow = () => {
  editForm.value.versions.push(toEditVersion());
};

const removeVersionRow = (index) => {
  editForm.value.versions.splice(index, 1);
  // 削除で添字がずれるため、残ったエラー表示はリセットする
  nextTick(() => editFormRef.value?.clearValidate("versions"));
};

/**
 * 全国スケジュールに出現した組み合わせを取り込む（既存と重複するものはスキップ）
 */
const importSuggestions = async () => {
  if (!editTarget.value) return;
  suggestLoading.value = true;
  try {
    const res = await getScheduleAiMovieVersionSuggestionsService(editTarget.value.movieId);
    const list = Array.isArray(res?.data) ? res.data : [];
    if (!list.length) {
      ElMessage.info("まだ上映スケジュールがありません");
      return;
    }
    // 未入力の空行は取り込み時に除く
    const rows = editForm.value.versions.filter(
      (v) => v.versionId !== null || v.dimension || v.screenFormat || v.language || v.special,
    );
    const keys = new Set(rows.map(versionKey));
    let added = 0;
    list.forEach((s) => {
      const v = toEditVersion({ ...s, versionId: null });
      if (keys.has(versionKey(v))) return;
      keys.add(versionKey(v));
      rows.push(v);
      added += 1;
    });
    if (!added) {
      ElMessage.info("追加できる組み合わせはありません（すべて登録済みです）");
      return;
    }
    editForm.value.versions = rows;
    ElMessage.success(`${added}件のバージョンを取り込みました`);
    nextTick(() => editFormRef.value?.validateField("versions").catch(() => {}));
  } catch {
    // エラーメッセージは request.js でトースト済み
  } finally {
    suggestLoading.value = false;
  }
};

// ==================== バリデーション ====================

// required キーを持たせて el-form-item の自動必須ルール（英語の既定メッセージ）を出さない。
// :required の値は Element Plus がこのルールの required に反映し、必須判定は validator で行う
const durationRule = {
  required: false,
  validator: (_rule, value, callback) => {
    if (value === undefined || value === null || value === "") {
      if (durationRequired.value) return callback(new Error("作品マスタに上映時間がないため、上映時間を入力してください"));
      return callback();
    }
    if (!Number.isInteger(value) || value < DURATION_MIN || value > DURATION_MAX) {
      return callback(new Error(`上映時間は${DURATION_MIN}〜${DURATION_MAX}分の整数で入力してください`));
    }
    return callback();
  },
  trigger: "change",
};

const periodRule = {
  validator: (_rule, _value, callback) => {
    const { localStartDate, localEndDate } = editForm.value;
    if (localStartDate && localEndDate && localStartDate > localEndDate) {
      return callback(new Error("終了日は開始日以降の日付を指定してください"));
    }
    return callback();
  },
  trigger: "change",
};

const versionsRule = {
  validator: (_rule, value, callback) => {
    const list = value || [];
    if (!list.length) return callback(new Error("上映バージョンを1件以上登録してください"));
    const incompleteIndex = list.findIndex((v) => !v.dimension || !v.screenFormat || !v.language);
    if (incompleteIndex !== -1) {
      return callback(new Error(`${incompleteIndex + 1}行目：2D/3D・上映方式・言語を選択してください`));
    }
    const seen = new Map();
    for (let i = 0; i < list.length; i += 1) {
      const key = versionKey(list[i]);
      if (seen.has(key)) {
        return callback(new Error(`${seen.get(key) + 1}行目と${i + 1}行目のバージョン「${versionLabel(list[i])}」が重複しています`));
      }
      seen.set(key, i);
    }
    return callback();
  },
  trigger: "change",
};

// 行の選択を変えたら重複・未入力チェックをやり直す
const onVersionChange = () => {
  editFormRef.value?.validateField("versions").catch(() => {});
};

const onPeriodChange = () => {
  editFormRef.value?.validateField("period").catch(() => {});
};

// ==================== 保存 ====================

const handleEditSave = async () => {
  try {
    await editFormRef.value.validate();
  } catch (fields) {
    const first = fields && Object.values(fields)[0]?.[0];
    if (first?.message) ElMessage.error(first.message);
    return;
  }
  editSaving.value = true;
  try {
    await updateScheduleAiMovieService(editTarget.value.id, buildEditPayload());
    ElMessage.success("保存しました");
    closeEdit();
    // 新規バージョンの versionId を受け取るため一覧を取り直す
    await fetchList();
  } catch {
    // 業務チェックのエラーメッセージは request.js でトースト済み
  } finally {
    editSaving.value = false;
  }
};

onMounted(fetchAll);
</script>

<template>
  <div v-loading="loading" class="movie-page">
    <header class="page-bar">
      <div class="page-bar__title">
        <h2>本館上映作品・バージョン登録</h2>
      </div>
    </header>

    <el-empty v-if="loadErrorMsg && !loading" :description="loadErrorMsg">
      <el-button @click="fetchAll">再読み込み</el-button>
    </el-empty>

    <template v-else-if="!loading">
      <!-- 作品検索・登録 -->
      <section class="panel">
        <div class="panel-head">
          <h3>作品を探して登録</h3>
          <span class="panel-note">作品マスタからタイトルで検索します</span>
        </div>
        <div class="search-bar">
          <el-input
            v-model="keyword"
            clearable
            maxlength="100"
            placeholder="作品タイトルを入力"
            class="search-input"
            @keyup.enter="handleSearch"
            @clear="clearSearch"
          >
            <template #prefix>
              <el-icon><Search /></el-icon>
            </template>
          </el-input>
          <el-button type="primary" :loading="searchLoading" @click="handleSearch">検索</el-button>
        </div>

        <div v-if="searchedKeyword" v-loading="searchLoading" class="search-result">
          <el-empty v-if="!searchRows.length && !searchLoading" description="該当する作品がありません" :image-size="64" />
          <ul v-else class="result-list">
            <li v-for="row in searchRows" :key="row.movieId" class="result-item">
              <el-image
                v-if="row.posterUrl"
                :src="row.posterUrl"
                fit="cover"
                lazy
                class="poster"
              />
              <span v-else class="poster poster--empty">No Image</span>
              <div class="result-main">
                <div class="result-title">{{ row.title }}</div>
                <div class="result-meta">
                  <span>公開日 {{ row.openDate || "未定" }}</span>
                  <span>{{ row.durationMinute ? `${row.durationMinute}分` : "上映時間 不明" }}</span>
                  <span class="muted">ID {{ row.movieId }}</span>
                </div>
              </div>
              <div class="result-actions">
                <el-button
                  size="small"
                  :aria-expanded="detailMovieId === row.movieId"
                  @click="toggleDetail(row)"
                >
                  {{ detailMovieId === row.movieId ? "閉じる" : "詳細" }}
                  <el-icon class="el-icon--right">
                    <ArrowUp v-if="detailMovieId === row.movieId" />
                    <ArrowDown v-else />
                  </el-icon>
                </el-button>
                <el-button
                  v-if="row.registered"
                  disabled
                  size="small"
                >
                  登録済
                </el-button>
                <el-button
                  v-else
                  type="primary"
                  plain
                  size="small"
                  :loading="addingMovieId === row.movieId"
                  :disabled="addingMovieId !== null && addingMovieId !== row.movieId"
                  @click="handleAdd(row)"
                >
                  登録
                </el-button>
              </div>
              <!-- 作品の実績（本館上映作品一覧の展開行と同じ内容） -->
              <div v-if="detailMovieId === row.movieId" class="result-insight">
                <MovieInsight :movie-id="row.movieId" />
              </div>
            </li>
          </ul>
          <Pagination
            v-show="searchTotal > 0"
            v-model:page="searchQuery.pageNum"
            v-model:limit="searchQuery.pageSize"
            :total="searchTotal"
            :auto-scroll="false"
            layout="total, prev, pager, next"
            @pagination="runSearch"
          />
        </div>
      </section>

      <!-- 本館上映作品一覧 -->
      <section class="panel">
        <div class="panel-head">
          <h3>本館上映作品<span class="panel-count">{{ movies.length }}</span></h3>
          <span class="panel-note">「興行分析データ」から作品の実績（観客属性・地域の充填率・本館の実績）を確認できます</span>
        </div>

        <el-empty v-if="!movies.length" description="登録された作品がありません。上の検索から登録してください" :image-size="72" />

        <el-table
          v-else
          ref="movieTableRef"
          :data="movies"
          row-key="id"
          class="movie-table"
          @expand-change="onExpandChange"
        >
          <!-- 展開行の中身だけ使う。列と展開アイコンは CSS で隠し、操作列の「興行分析データ」で開閉する -->
          <el-table-column type="expand" width="1" class-name="expand-col" label-class-name="expand-col">
            <template #default="{ row }">
              <div class="expand-body">
                <MovieInsight :movie-id="row.movieId" />
              </div>
            </template>
          </el-table-column>
          <el-table-column label="作品ID" prop="movieId" width="90" />
          <el-table-column label="タイトル" min-width="220">
            <template #default="{ row }">
              <div class="title-cell">
                <span class="title-text">{{ row.title }}</span>
                <el-tag v-if="row.movieDeleted" type="warning" size="small" effect="plain">
                  マスタ削除済
                </el-tag>
              </div>
            </template>
          </el-table-column>
          <el-table-column label="公開日" width="110">
            <template #default="{ row }">{{ row.openDate || "未定" }}</template>
          </el-table-column>
          <el-table-column label="上映時間" width="90">
            <template #default="{ row }">
              <span v-if="row.durationMinute">{{ row.durationMinute }}分</span>
              <span v-else class="warn-text">未設定</span>
            </template>
          </el-table-column>
          <el-table-column label="本館上映期間" width="200">
            <template #default="{ row }">{{ periodLabel(row) }}</template>
          </el-table-column>
          <el-table-column label="上映バージョン" min-width="280">
            <template #default="{ row }">
              <ul v-if="row.versions?.length" class="ver-list">
                <li v-for="v in row.versions" :key="v.versionId ?? versionKey(v)" class="ver-chip">
                  <span class="ver-chip__dim">{{ v.dimension }}</span>
                  <span class="ver-chip__fmt">{{ v.screenFormat }}</span>
                  <span v-if="v.language" class="ver-chip__lang">{{ v.language }}</span>
                  <span v-if="v.special" class="ver-chip__sp">{{ v.special }}</span>
                </li>
              </ul>
              <span v-else class="warn-text">未登録</span>
            </template>
          </el-table-column>
          <!-- 「興行分析データ」の文言が長いため幅を広げる -->
          <el-table-column label="操作" width="280" fixed="right">
            <template #default="{ row }">
              <el-button link type="primary" :aria-expanded="expandedIds.has(row.id)" @click="toggleRowDetail(row)">
                {{ expandedIds.has(row.id) ? "閉じる" : "興行分析データ" }}
                <el-icon class="el-icon--right">
                  <ArrowUp v-if="expandedIds.has(row.id)" />
                  <ArrowDown v-else />
                </el-icon>
              </el-button>
              <el-button link type="primary" @click="openEdit(row)">編集</el-button>
              <el-button link type="danger" @click="handleRemove(row)">登録解除</el-button>
            </template>
          </el-table-column>
        </el-table>
      </section>
    </template>

    <!-- 編集ダイアログ -->
    <el-dialog
      v-model="editVisible"
      :title="editTarget ? `${editTarget.title} の設定` : ''"
      width="760px"
      append-to-body
      :close-on-click-modal="false"
      :before-close="beforeEditClose"
      class="movie-edit-dialog"
    >
      <el-form
        v-if="editTarget"
        ref="editFormRef"
        :model="editForm"
        label-position="top"
        @submit.prevent
      >
        <div class="edit-grid">
          <el-form-item label="本館上映期間" prop="period" :rules="periodRule">
            <div class="period-range">
              <el-date-picker
                v-model="editForm.localStartDate"
                type="date"
                value-format="YYYY-MM-DD"
                placeholder="開始日"
                class="period-date"
                @change="onPeriodChange"
              />
              <span class="period-sep">〜</span>
              <el-date-picker
                v-model="editForm.localEndDate"
                type="date"
                value-format="YYYY-MM-DD"
                placeholder="終了日"
                class="period-date"
                @change="onPeriodChange"
              />
            </div>
            <p class="hint">未入力の場合は全国公開日（{{ editTarget.openDate || "未定" }}）から上映として扱います</p>
          </el-form-item>

          <el-form-item
            label="上映時間（分）"
            prop="durationMinute"
            :required="durationRequired"
            :rules="durationRule"
          >
            <el-input-number
              v-model="editForm.durationMinute"
              :min="DURATION_MIN"
              :max="DURATION_MAX"
              :step="1"
              step-strictly
              controls-position="right"
              class="duration-input"
            />
            <p class="hint">
              <template v-if="durationRequired">作品マスタに上映時間がありません</template>
              <template v-else>作品マスタ：{{ editTarget.masterDurationMinute }}分</template>
            </p>
          </el-form-item>
        </div>

        <el-form-item prop="versions" :rules="versionsRule" required class="versions-item">
          <template #label>
            <span class="versions-label">
              上映バージョン
              <span class="versions-actions">
                <el-button size="small" :loading="suggestLoading" @click="importSuggestions">
                  スケジュールから取り込み
                </el-button>
                <el-button size="small" type="primary" plain @click="addVersionRow">行を追加</el-button>
              </span>
            </span>
          </template>
          <div class="ver-rows">
            <div class="ver-row ver-row--head" aria-hidden="true">
              <span>2D/3D</span>
              <span>上映方式</span>
              <span>言語</span>
              <span>特殊</span>
              <span />
            </div>
            <div v-for="(v, index) in editForm.versions" :key="v._key" class="ver-row">
              <el-select v-model="v.dimension" placeholder="2D/3D" @change="onVersionChange">
                <el-option v-for="d in options.dimensions" :key="d" :label="d" :value="d" />
              </el-select>
              <el-select v-model="v.screenFormat" filterable placeholder="上映方式" @change="onVersionChange">
                <el-option v-for="f in options.screenFormats" :key="f" :label="f" :value="f" />
              </el-select>
              <el-select v-model="v.language" placeholder="言語" @change="onVersionChange">
                <el-option v-for="l in options.languages" :key="l" :label="l" :value="l" />
              </el-select>
              <el-select v-model="v.special" clearable placeholder="なし" @change="onVersionChange">
                <el-option v-for="sp in options.specials" :key="sp" :label="sp" :value="sp" />
              </el-select>
              <el-button
                text
                type="danger"
                :aria-label="`${index + 1}行目を削除`"
                @click="removeVersionRow(index)"
              >
                <el-icon><Delete /></el-icon>
              </el-button>
            </div>
            <p v-if="!editForm.versions.length" class="hint">「行を追加」からバージョンを登録してください</p>
          </div>
        </el-form-item>
      </el-form>

      <template #footer>
        <el-button @click="beforeEditClose(() => {})">キャンセル</el-button>
        <el-button type="primary" :loading="editSaving" @click="handleEditSave">保存</el-button>
      </template>
    </el-dialog>
  </div>
</template>

<style lang="less" scoped>
/* ステップ1（setting/index.vue）と同じデザイントークン */
.movie-page {
  --sp-brand: var(--brand-color, #cc251b);
  --sp-panel: var(--bg-panel, #fff);
  --sp-border: var(--border-color, #e5e5e5);
  --sp-text: var(--text-primary, #000);
  --sp-text-2: var(--text-secondary, #4a4a4a);
  --sp-muted: var(--text-tertiary, #757575);
  --sp-tint: color-mix(in srgb, var(--sp-brand) 7%, var(--sp-panel));
  --sp-tint-strong: color-mix(in srgb, var(--sp-brand) 14%, var(--sp-panel));
  --sp-radius: var(--border-radius, 6px);

  max-width: 1280px;
  min-height: 200px;
  padding: 0 24px 40px 15px;
  box-sizing: border-box;
  color: var(--sp-text);
}

/* ---------- 操作バー ---------- */
.page-bar {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  padding: 15px 0 12px;

  &__title h2 {
    margin: 0;
    font-size: 18px;
    font-weight: 700;
  }
}

/* ---------- パネル共通 ---------- */
.panel {
  margin-bottom: 16px;
  border: 1px solid var(--sp-border);
  border-radius: var(--sp-radius);
  background: var(--sp-panel);
  box-shadow: var(--shadow-sm);
  overflow: hidden;
}

.panel-head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  flex-wrap: wrap;
  padding: 12px 18px;
  border-bottom: 1px solid var(--sp-border);
  background: var(--sp-tint);

  h3 {
    margin: 0;
    font-size: 15px;
    font-weight: 700;
  }
}

.panel-count {
  margin-left: 8px;
  font-weight: 500;
  font-variant-numeric: tabular-nums;
  color: var(--sp-muted);
}

.panel-note {
  font-size: 12px;
  color: var(--sp-muted);
}

.hint {
  width: 100%;
  margin: 4px 0 0;
  font-size: 12px;
  line-height: 1.6;
  color: var(--sp-muted);
}

.muted {
  color: var(--sp-muted);
}

.warn-text {
  font-size: 12px;
  color: var(--el-color-warning);
}

/* ---------- 検索 ---------- */
.search-bar {
  display: flex;
  gap: 8px;
  padding: 14px 18px;

  .search-input {
    max-width: 420px;
  }
}

.search-result {
  min-height: 60px;
  padding: 0 18px 12px;
}

// 全局（ruoyi.scss）の分页条は absolute で最寄りの定位祖先に寄るためパネル外へはみ出す。本ページでは通常フローで右寄せにする
.search-result :deep(.pagination-container) {
  display: flex;
  justify-content: flex-end;
  height: auto;
  margin: 8px 0 0;
  padding: 8px 0 !important;
  background: transparent;

  .el-pagination {
    position: static;
    flex-wrap: wrap;
  }
}

.result-list {
  margin: 0;
  padding: 0;
  list-style: none;
}

.result-item {
  display: grid;
  grid-template-columns: 44px minmax(0, 1fr) auto;
  align-items: center;
  gap: 12px;
  padding: 8px 0;

  & + & {
    border-top: 1px solid var(--sp-border);
  }
}

.poster {
  width: 44px;
  height: 62px;
  border-radius: 3px;
  background: color-mix(in srgb, var(--sp-muted) 10%, var(--sp-panel));

  &--empty {
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 9px;
    text-align: center;
    color: var(--sp-muted);
  }
}

.result-main {
  min-width: 0;
}

.result-actions {
  display: flex;
  align-items: center;
  gap: 8px;

  .el-button + .el-button {
    margin-left: 0;
  }
}

// 詳細は行の下に全幅で表示
.result-insight {
  grid-column: 1 / -1;
  padding: 8px 12px;
  border-radius: var(--sp-radius);
  background: var(--sp-tint);
}

.result-title {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  font-size: 14px;
  font-weight: 600;
}

.result-meta {
  display: flex;
  flex-wrap: wrap;
  gap: 2px 14px;
  margin-top: 2px;
  font-size: 12px;
  font-variant-numeric: tabular-nums;
  color: var(--sp-text-2);
}

/* ---------- 本館上映作品一覧 ---------- */
.title-cell {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 6px;
}

.title-text {
  font-weight: 600;
}

.movie-table {
  // 展開列は幅 1px にして中身（展開アイコン）を隠す
  :deep(.expand-col) {
    padding: 0;
    border-right: none;

    .cell {
      display: none;
    }
  }
}

.expand-body {
  padding: 8px 16px 12px 48px;
  background: var(--sp-tint);
}

.ver-list {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
  margin: 0;
  padding: 0;
  list-style: none;
}

.ver-chip {
  display: inline-flex;
  align-items: stretch;
  overflow: hidden;
  border: 1px solid color-mix(in srgb, var(--sp-brand) 35%, var(--sp-border));
  border-radius: 4px;
  font-size: 12px;
  line-height: 22px;
  background: var(--sp-panel);

  &__dim {
    padding: 0 6px;
    font-weight: 700;
    color: #fff;
    background: var(--sp-brand);
  }

  &__fmt {
    padding: 0 7px;
    font-weight: 500;
  }

  &__lang,
  &__sp {
    padding: 0 7px;
    border-left: 1px solid var(--sp-border);
  }

  &__sp {
    color: var(--sp-brand);
  }
}

/* ---------- 編集ダイアログ ---------- */
.edit-grid {
  display: grid;
  grid-template-columns: minmax(0, 1fr) 200px;
  gap: 0 20px;
}

.period-range {
  display: flex;
  align-items: center;
  gap: 8px;
  width: 100%;

  .period-date {
    flex: 1;
    width: auto;
  }
}

.period-sep {
  color: var(--sp-muted);
}

.duration-input {
  width: 100%;
}

.versions-item :deep(.el-form-item__label) {
  width: 100%;
  padding-right: 0;
}

.versions-label {
  display: inline-flex;
  align-items: center;
  justify-content: space-between;
  width: calc(100% - 12px);
}

.versions-actions .el-button {
  margin-left: 8px;
}

.ver-rows {
  display: flex;
  flex-direction: column;
  gap: 8px;
  width: 100%;
}

.ver-row {
  display: grid;
  grid-template-columns: 96px minmax(0, 1.4fr) minmax(0, 1fr) minmax(0, 1fr) 40px;
  align-items: center;
  gap: 8px;

  &--head {
    font-size: 12px;
    line-height: 1;
    color: var(--sp-muted);
  }
}

@media (max-width: 768px) {
  .movie-page {
    padding: 0 12px 32px;
  }

  .search-bar {
    padding: 12px;
  }

  .search-result {
    padding: 0 12px 12px;
  }

  .edit-grid {
    grid-template-columns: 1fr;
  }

  .ver-row {
    grid-template-columns: repeat(2, minmax(0, 1fr));

    &--head {
      display: none;
    }
  }
}
</style>
